import { supabase } from './supabaseClient';

export const FORM_DRAFT_ACTIVE_STATUS = 'active';
export const FORM_DRAFT_DISCARDED_STATUS = 'discarded';
export const FORM_DRAFT_SUBMITTED_STATUS = 'submitted';
export const FORM_DRAFT_DEFAULT_VERSION = 'v1';
export const FORM_DRAFT_DEFAULT_TTL_DAYS = 30;
export const FORM_DRAFT_SELECT_FIELDS = [
  'id',
  'form_key',
  'entity_id',
  'form_version',
  'payload',
  'status',
  'last_saved_at',
  'expires_at',
  'created_at',
  'updated_at',
].join(', ');

function trimText(value) {
  return String(value ?? '').trim();
}

export function normalizeFormDraftEntityId(entityId) {
  const normalizedEntityId = trimText(entityId);
  return normalizedEntityId || null;
}

export function buildFormDraftExpiresAt({ now = new Date(), ttlDays = FORM_DRAFT_DEFAULT_TTL_DAYS } = {}) {
  const nextExpiration = new Date(now.getTime() + (ttlDays * 24 * 60 * 60 * 1000));
  return nextExpiration.toISOString();
}

export function buildAarFormDraftPayload({
  formValues = {},
  source = 'aar_new',
  savedAt = new Date().toISOString(),
} = {}) {
  return {
    formValues: formValues && typeof formValues === 'object' ? { ...formValues } : {},
    savedAt,
    source: trimText(source) || 'aar_new',
  };
}

function buildActiveFormDraftQuery({
  query,
  formKey,
  entityId = null,
  formVersion = FORM_DRAFT_DEFAULT_VERSION,
}) {
  const normalizedEntityId = normalizeFormDraftEntityId(entityId);
  const baseQuery = query
    .eq('form_key', trimText(formKey))
    .eq('form_version', trimText(formVersion) || FORM_DRAFT_DEFAULT_VERSION)
    .eq('status', FORM_DRAFT_ACTIVE_STATUS);

  return normalizedEntityId
    ? baseQuery.eq('entity_id', normalizedEntityId)
    : baseQuery.is('entity_id', null);
}

export async function fetchActiveFormDraft({
  formKey,
  entityId = null,
  formVersion = FORM_DRAFT_DEFAULT_VERSION,
  client = supabase,
} = {}) {
  if (!trimText(formKey)) return null;

  const { data, error } = await buildActiveFormDraftQuery({
    query: client.from('form_drafts').select(FORM_DRAFT_SELECT_FIELDS),
    formKey,
    entityId,
    formVersion,
  }).maybeSingle();

  if (error) throw error;
  return data ?? null;
}

export async function saveFormDraft({
  draftId = null,
  userId,
  formKey,
  entityId = null,
  formVersion = FORM_DRAFT_DEFAULT_VERSION,
  payload,
  client = supabase,
  now = new Date(),
} = {}) {
  if (!trimText(userId)) throw new Error('A signed-in user is required to save a draft.');
  if (!trimText(formKey)) throw new Error('A form key is required to save a draft.');

  const draftRecord = {
    user_id: userId,
    form_key: trimText(formKey),
    entity_id: normalizeFormDraftEntityId(entityId),
    form_version: trimText(formVersion) || FORM_DRAFT_DEFAULT_VERSION,
    payload: payload && typeof payload === 'object' ? payload : {},
    status: FORM_DRAFT_ACTIVE_STATUS,
    last_saved_at: now.toISOString(),
    expires_at: buildFormDraftExpiresAt({ now }),
  };

  if (trimText(draftId)) {
    const { data, error } = await client
      .from('form_drafts')
      .update(draftRecord)
      .eq('id', draftId)
      .select(FORM_DRAFT_SELECT_FIELDS)
      .single();

    if (error) throw error;
    return data ?? null;
  }

  const { data, error } = await client
    .from('form_drafts')
    .insert(draftRecord)
    .select(FORM_DRAFT_SELECT_FIELDS)
    .single();

  if (error) throw error;
  return data ?? null;
}

export async function markFormDraftStatus({
  draftId,
  status,
  client = supabase,
} = {}) {
  if (!trimText(draftId) || !trimText(status)) return null;

  const { data, error } = await client
    .from('form_drafts')
    .update({ status: trimText(status) })
    .eq('id', draftId)
    .select(FORM_DRAFT_SELECT_FIELDS)
    .single();

  if (error) throw error;
  return data ?? null;
}

export function readFormDraftFormValues(row = null) {
  const formValues = row?.payload?.formValues;
  if (!formValues || typeof formValues !== 'object' || Array.isArray(formValues)) return null;
  return formValues;
}

export function readFormDraftSavedAt(row = null) {
  return row?.last_saved_at
    || row?.payload?.savedAt
    || row?.updated_at
    || row?.created_at
    || null;
}
